<template>
  <div class="callback-container">
    <div v-if="loading" class="loading">
      <h2>正在处理微信官方账号登录...</h2>
      <div class="spinner"></div>
    </div>
    <div v-else-if="error" class="error">
      <h2>登录失败</h2>
      <p>{{ error }}</p>
      <button @click="goLogin">返回登录</button>
    </div>
    <div v-else class="success">
      <h2>登陆成功!</h2>
      <p>跳转中...</p>
    </div>
  </div>
</template>

<script>
import authManager from '@/utils/auth'
import { clearWechatSkipAutoLogin } from '@/utils/loginFlow'
import { handleGuestClaimAfterAuth } from '@/utils/examClaim'
import {
  decodeWechatOfficialRedirectPath,
  exchangeOfficialOAuthCode,
  isSignedWxOAuthState
} from '@/utils/officialOAuth'
import { WX_LOGIN_MANUAL_KEY } from '@/utils/browser'
import { resolvePostLoginRoute } from '@/utils/referralAuth'
import {
  accountSetupRoute,
  markAccountSetupPrompted,
  requiresInitialAccountSetup,
  shouldForceAccountSetupOnLogin
} from '@/utils/accountSetup'

export default {
  name: 'OfficialCallbackView',
  data() {
    return {
      loading: true,
      error: null
    }
  },
  async created() {
    const code = this.$route.query.code
    const state = this.$route.query.state || ''

    try {
      if (!code) {
        const href = decodeWechatOfficialRedirectPath(state) || this.$route.query.href
        this.$router.replace(typeof href === 'string' && href.startsWith('/') ? href : '/')
        return
      }

      // 匿名测评 base 流程：后端 302 跳转，必须整页请求 API
      if (isSignedWxOAuthState(state)) {
        const qs = new URLSearchParams({ code, state })
        window.location.replace(`/api/v1/wechat/official/callback?${qs.toString()}`)
        return
      }

      const { loginData, redirectURL, couponBindStatus, couponBindMessage } = await exchangeOfficialOAuthCode(code, state)

      // 新用户未完善登录名/密码：本次登录直接跳转完善页
      // （markAccountSetupPrompted 延迟到确定跳转时再设置：若走测评 claim 流程
      //   直接进入报告页，此时不应抑制 App.login 的完善引导弹窗）
      const needSetup = requiresInitialAccountSetup(loginData)
      clearWechatSkipAutoLogin()
      await authManager.handleLoginSuccess(loginData)
      this.$root.login(loginData)

      if (couponBindStatus === 'failed' && couponBindMessage) {
        this.$message.warning(`礼券绑定失败：${couponBindMessage}`)
      } else if (couponBindStatus === 'ok') {
        this.$message.success('礼券已绑定成功')
      }

      const claimStatus = await handleGuestClaimAfterAuth(this.$router, this.$route)
      if (claimStatus === 'claimed' || claimStatus === 'recharge') {
        return
      }

      const href = typeof redirectURL === 'string' && redirectURL.startsWith('/') ? redirectURL : '/'

      // 未完善且无明确回跳（仅首页）：强制完善页；带着具体 href 则直接去目标页
      if (needSetup && shouldForceAccountSetupOnLogin(href)) {
        markAccountSetupPrompted()
        this.$router.replace(accountSetupRoute(href))
        return
      }
      if (needSetup) {
        markAccountSetupPrompted()
      }

      const target = await resolvePostLoginRoute(href, {
        skipReferralForExam: href.includes('/exam/')
      })
      this.$router.replace(target)
    } catch (err) {
      console.error('Official account callback error:', err)
      if (err?.code === 'oauth_code_used') {
        this.error = '微信授权已失效，请返回登录页重新点击「微信一键登录」'
      } else if (err?.response?.data?.message) {
        this.error = err.response.data.message
      } else {
        this.error = err.message || '微信登录失败，请重试'
      }
    } finally {
      this.loading = false
    }
  },
  methods: {
    goLogin() {
      sessionStorage.setItem(WX_LOGIN_MANUAL_KEY, '1')
      const href = decodeWechatOfficialRedirectPath(this.$route.query.state || '')
      const query = { manual: '1' }
      if (href) query.href = href
      this.$router.replace({ path: '/login', query })
    }
  }
}
</script>

<style scoped>
.callback-container {
  max-width: 600px;
  margin: 100px auto;
  padding: 20px;
  text-align: center;
}

.loading, .error, .success {
  padding: 20px;
  border-radius: 8px;
  background-color: #f8f9fa;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 20px auto;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #42b983;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  color: #dc3545;
}

.error button {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.error button:hover {
  background-color: #c82333;
}

.success {
  color: #28a745;
}
</style>
